import { ClickAwayListener } from "@nova/component-click-away-listener"
import { IconComponent } from "@nova/component-icons"
import { ID } from "@nova/constants"
import classNames from "classnames"
import { memo, MouseEvent, useEffect, useRef } from "react"
import { FlyoutMenuTab } from "./FlyoutMenuTab"
import { FlyoutMenuActionTab } from "./FlyoutMenuActionTab"
import { ScrollableArea } from "@nova/component-scrollable-area"

export type FlyoutMenuContentItem = {
    id: ID
    title: string
    onClick: (id: ID) => void
}

export type FlyoutMenuActionItem = {
    title: string
    icon: IconComponent
    shouldShowExitIcon?: boolean
    onClick: (e: MouseEvent) => void
}

type FlyoutMenuProps = {
    title: string
    className?: string

    contentItems?: FlyoutMenuContentItem[]

    /** ID of active content item, if at all. */
    activeId?: ID

    /**
     * 1st dimension is section, 2nd is items in section.
     */
    actionSections?: FlyoutMenuActionItem[][]

    onBlur: () => void
}

const FlyoutMenu = memo(
    ({ title, className, contentItems, activeId, actionSections, onBlur }: FlyoutMenuProps) => {
        const activeTab = useRef<HTMLDivElement | null>(null)
        // Scroll to the active workspace in the scrollable list
        useEffect(() => {
            activeTab.current?.scrollIntoView({ behavior: "auto", block: "center" })
        }, [activeId])

        return (
            <ClickAwayListener
                onClickAway={onBlur}
                className={classNames(
                    "absolute bg-gray-700 px-2 pt-2 pb-1 rounded-2xl z-50 shadow-card cursor-default",
                    className
                )}
            >
                <FlyoutMenuTab title={title} isSelectable={false} />

                <ScrollableArea className="max-h-[20rem]">
                    {contentItems &&
                        contentItems.map((item) => (
                            <FlyoutMenuTab
                                ref={item.id === activeId ? activeTab : undefined}
                                key={item.id}
                                id={item.id}
                                title={item.title}
                                isActive={item.id === activeId}
                                isSelectable={true}
                                onTabClick={item.onClick}
                            />
                        ))}
                </ScrollableArea>
                {actionSections &&
                    actionSections.map((section, i) => (
                        // Show separator for each section, but exclude it on the first
                        // section ONLY if there are not normal content tabs above it.
                        <div
                            key={i}
                            className={classNames("w-full", {
                                "border-t border-black-300": contentItems || i,
                            })}
                        >
                            {section.map((item) => (
                                <FlyoutMenuActionTab
                                    key={item.title}
                                    title={item.title}
                                    icon={item.icon}
                                    shouldShowExitIcon={item.shouldShowExitIcon}
                                    onClick={item.onClick}
                                />
                            ))}
                        </div>
                    ))}
            </ClickAwayListener>
        )
    }
)
FlyoutMenu.displayName = "FlyoutMenu"
export { FlyoutMenu }
